/**
 * One of Tournament Configuration's eight section cards (`07-configuration.md` §2, and
 * `docs/design/configuration-page-v1.html`, which is the design authority for this page).
 *
 * **Why this component exists.** The mockup draws module 07 as *one long page of eight
 * collapsible section cards in setup order* — each a `.cfg` card whose head is a single
 * button carrying a two-digit number, a title, a sub-line, a spacer, a status pill, a
 * `Locked At Go-Live` chip and a chevron, with a `.cfg__body` under it and a `.cfg__foot`
 * beneath that saying what happens to the section at go-live. The built page drew four flat
 * `.panel`s with no numbering, no order and no statement of what the whole page contains, so
 * a reader had no way to tell an absent section from one that had scrolled past.
 *
 * **The head is one button, and the whole head is the target.** The mockup makes the entire
 * `.cfg__head` the control — `aria-expanded` and `aria-controls` on the button itself — so a
 * reader aiming at a section title hits the thing that opens it rather than a small chevron
 * at the far right of a wide card.
 *
 * **The body is unmounted when collapsed, not hidden.** The mockup uses `[hidden]`, which is
 * right for a static file. Here the bodies carry live-bound controls with their own state and
 * their own labels; leaving a collapsed section's controls in the DOM would put a second
 * `Save`, a second `Team Exposure Heat Threshold` and a second of every other label on the
 * page, where anything reading the page by its text — a screen reader, a test — would find
 * the hidden copy. Collapsed means gone, and reopening re-renders from the same props.
 *
 * **What this component refuses to invent.** It draws a status pill and a lock chip only when
 * it is given one. A section this build has no data for is handed a muted pill saying so,
 * never the mockup's hard-coded `Complete`, and never a `Locked At Go-Live` chip: no go-live
 * state exists on the wire yet, so a lock chip here would be a claim about a lifecycle the
 * system cannot observe.
 */

export type ConfigSectionTone = "good" | "warn" | "accent" | "muted" | "alert";

export interface ConfigSectionProps {
  /** `01` … `08` — the two-digit number the mockup prints in `.cfg__n`. */
  number: string;
  /** `Appointment Rules`. */
  title: string;
  /** `Ownership, Hard Blocks, Soft Warnings, Thresholds`. */
  subtitle: string;
  /** The head's status pill. Omitted, no pill is drawn at all. */
  status?: { label: string; tone: ConfigSectionTone };
  /**
   * The mockup's `Locked At Go-Live` chip, hidden until the tournament is live. This build
   * has no go-live signal, so nothing passes it yet; the prop exists so that the section that
   * gains one does not have to grow a new idiom for it.
   */
  lockChip?: string;
  /** Whether the section is open. Controlled, so `Expand All` can reach every one at once. */
  expanded: boolean;
  onToggle: (expanded: boolean) => void;
  /**
   * Set when the body holds whole `.panel`s rather than plain fields. The card then drops its
   * own body padding and the panels lose their frames, so a nested panel reads as a block
   * inside this card and not as a second card drawn inside the first.
   */
  bodyHoldsPanels?: boolean;
  /** The `.cfg__foot` line — what happens to this section at go-live. */
  foot: React.ReactNode;
  children: React.ReactNode;
}

export function ConfigSection({
  number,
  title,
  subtitle,
  status,
  lockChip,
  expanded,
  onToggle,
  bodyHoldsPanels = false,
  foot,
  children,
}: ConfigSectionProps): React.JSX.Element {
  const bodyId = `cfgSec${number}Body`;

  return (
    <section
      className={`cfg${lockChip === undefined ? "" : " is-locked"}`}
      data-howto={`section-${number}`}
      data-testid={`config-section-${number}`}
    >
      <button
        type="button"
        className="cfg__head"
        aria-expanded={expanded}
        aria-controls={bodyId}
        data-testid={`config-section-toggle-${number}`}
        onClick={() => {
          onToggle(!expanded);
        }}
      >
        <span className="cfg__n">{number}</span>
        <span className="cfg__t">{title}</span>
        <span className="cfg__sub">{subtitle}</span>
        <span className="spacer" />
        {status === undefined ? null : (
          <span className={`statuspill statuspill--${status.tone}`}>
            <span className="sdot" aria-hidden="true" />
            {status.label}
          </span>
        )}
        {lockChip === undefined ? null : <span className="lockchip">{lockChip}</span>}
        <span className="cfg__chev" aria-hidden="true">
          ⌄
        </span>
      </button>

      {expanded ? (
        <div className={bodyHoldsPanels ? "cfg__body cfg__body--panels" : "cfg__body"} id={bodyId}>
          {children}
        </div>
      ) : null}

      <div className="cfg__foot">{foot}</div>
    </section>
  );
}

/**
 * The body of a section this build has no data layer for.
 *
 * It states what the section will hold and why it is not here, in the mockup's own `.notebox`
 * idiom — never an empty form. `07-configuration.md` is explicit about the failure this
 * avoids: a configuration field that renders but stores nothing invites a Technical Delegate
 * to set a value the tournament will never read.
 */
export function ConfigSectionAbsent({
  holds,
  reason,
}: {
  /** What the section holds in the design, in the mockup's own words. */
  holds: string;
  /** Why it is not built yet. */
  reason: string;
}): React.JSX.Element {
  return (
    <div className="notebox" data-testid="config-section-absent">
      <b>Not on this page yet.</b> This section holds {holds}. {reason} It is drawn as an empty
      section rather than as a form, because a field that stores nothing is worse than one that
      is missing.
    </div>
  );
}
